/**
 * pages/Activate.js — Iter-262b
 *
 * Account activation landing page. Closes the P0 reported on 2026-06-17
 * where the activation email pointed to a route that didn't exist.
 *
 * Two modes:
 *   • `?token=…` (new, secure)  → POST /api/auth/activate with token
 *   • `?email=…` (legacy, for in-flight emails already sent before the
 *                  fix shipped) → POST /api/auth/activate-by-email
 *
 * Always lands the user on /login after success.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import api from '../utils/api';

export default function Activate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: 'loading', message: '', email: null });

  useEffect(() => {
    const token = params.get('token');
    const email = params.get('email');

    if (!token && !email) {
      setState({ phase: 'error', message: 'This activation link is missing a token. Please contact support.' });
      return;
    }

    (async () => {
      try {
        const res = token
          ? await api.post('/auth/activate',          { token })
          : await api.post('/auth/activate-by-email', { email });
        setState({
          phase: 'success',
          message: res.data?.message || 'Your account is now active.',
          email:   res.data?.email || email,
        });
      } catch (err) {
        setState({
          phase: 'error',
          message: err?.response?.data?.detail || 'We couldn\'t activate your account. The link may have expired.',
        });
      }
    })();
  }, [params]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-violet-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-lg" data-testid="activate-page">
        <CardContent className="p-8">
          {state.phase === 'loading' && (
            <div className="text-center space-y-3" data-testid="activate-loading">
              <Loader2 className="w-10 h-10 text-cyan-500 animate-spin mx-auto" />
              <h1 className="text-xl font-semibold text-gray-900">Activating your account…</h1>
              <p className="text-sm text-gray-500">Hang tight, this only takes a second.</p>
            </div>
          )}
          {state.phase === 'success' && (
            <div className="text-center space-y-4" data-testid="activate-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
              <h1 className="text-2xl font-bold text-gray-900">You're all set</h1>
              <p className="text-sm text-gray-600">{state.message}</p>
              {state.email && (
                <p className="text-xs text-gray-500">Logging in as <span className="font-medium">{state.email}</span></p>
              )}
              <Button
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                onClick={() => navigate('/login')}
                data-testid="activate-success-login-btn"
              >
                Continue to login
              </Button>
            </div>
          )}
          {state.phase === 'error' && (
            <div className="text-center space-y-4" data-testid="activate-error">
              <AlertCircle className="w-14 h-14 text-rose-500 mx-auto" />
              <h1 className="text-2xl font-bold text-gray-900">Activation problem</h1>
              <p className="text-sm text-gray-600">{state.message}</p>
              <div className="space-y-2 pt-2">
                <Button
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                  onClick={() => navigate('/login')}
                  data-testid="activate-error-login-btn"
                >
                  Go to login
                </Button>
                <Link to="/forgot-password" className="block text-xs text-cyan-600 hover:underline">
                  Forgot your password? Reset it here.
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
